/**
 * Sydney's YELKENLİ — a harbour yacht — in the shared 120×120 LINE asset box.
 *
 * The Home tab, all four passcode dots and the chat wallpaper draw this boat.
 *
 * WHY THIS REPLACED THE OPERA HOUSE
 *
 * The building was drawn twice: once with pointed shells, which the owner sent
 * back as too steep, and once with blunt ones, which was a better drawing of the
 * same wrong thing. It is a landmark, and a landmark is a postcard — you look at
 * it once. Sydney Harbour on any given afternoon is a few hundred boats, and a
 * boat is a thing that is DOING something, which is a friendlier register for a
 * chat theme than a monument is.
 *
 * It also settles a problem the building never could. A wallpaper wants the hero
 * repeated twenty times; the Opera House at that size is a grey clump, and one
 * shell of it, tilted over pale water, reads as a SHARK FIN — in an Australian
 * theme. Twenty small boats on a harbour is simply a harbour.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - TWO SAILS, NOT ONE. A single triangle on a hull is a dinghy in a clip-art
 *    set. A mainsail behind the mast with a jib in front of it is a yacht, and the
 *    pair is what nobody has to be told the name of.
 *  - THE LEECHES BOW OUT. Both sails' trailing edges are convex, because a sail
 *    with wind in it is a curve. Drawn as straight-edged triangles they are
 *    bunting.
 *  - THE HULL IS A CRESCENT, blunt at the stern and lifted at the bow. A
 *    rectangle under the sails is a raft.
 *  - THE TWO SAILS ARE DIFFERENT SIZES and lean the same way. Matched, they make
 *    one symmetrical arrowhead — the failure Madrid's churros and Etna's lava
 *    tongues both had before them.
 *
 * Every subpath is wound clockwise so a nonzero fill unions the overlaps instead
 * of punching holes in them ([[line-shared-glyph-modules]]).
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

/** The deck line — everything above this is rig, everything below is hull. */
const DECK = 86;
/** Where the mast stands, and how far up it goes. */
const MAST_X = 61, MAST_TOP = 16;

/**
 * The HULL: a crescent, blunt at the stern and lifted at the bow.
 *
 * The sheer line — the top edge — RISES toward the bow rather than running level.
 * Level, the boat sits in the water like a box; the lift is what says it is
 * moving, and it is four units.
 */
export const YACHT_HULL =
  `M10,${ DECK - 2 }`
  + ` C34,${ DECK + 2 } 78,${ DECK + 2 } 104,${ DECK - 6 }`   // the sheer, rising to the bow
  // SHALLOWER than the first pass, which ran 23 units from sheer to keel and came
  // back as a bowl with a mast in it. A sailing hull shows very little of itself
  // above the water; at 18 it is a boat, and past about 20 it is a banana.
  + ' C101,89 91,96 74,99'                                     // the bow, down to the keel
  + ' C55,102 25,95 10,84 Z';                                  // the keel back to the stern

/**
 * The MAST and the BOOM as one path.
 *
 * Both are thin — two and a half units — and they are the only straight lines in
 * the drawing. That is deliberate: everything else here is a curve, so the rig
 * reads as structure and the sails read as cloth.
 */
export const YACHT_MAST = [
  cw( [ [ MAST_X - 1.3, DECK ], [ MAST_X - 1.3, MAST_TOP ],
        [ MAST_X + 1.3, MAST_TOP ], [ MAST_X + 1.3, DECK ] ] ),
  cw( [ [ 22, DECK + 0.5 ], [ 22, DECK - 3 ],
        [ MAST_X + 2, DECK - 3 ], [ MAST_X + 2, DECK + 0.5 ] ] ),
].join( ' ' );

/**
 * The MAINSAIL: the big one, behind the mast.
 *
 * Its luff is the mast, its foot is the boom, and its leech — the long edge from
 * the masthead down to the clew — bows OUT to the left. The bulge is about six
 * units off the chord and it is the whole difference between a sail and a pennant.
 */
export const YACHT_MAIN =
  `M${ MAST_X },18`
  // The bulge is now about ten units off the chord, not six. At six the leech read
  // as a straight edge with a wobble in it, which is a pennant.
  + ` C46,32 28,58 22,${ DECK + 1 }`     // the leech, convex
  + ` L${ MAST_X },${ DECK + 1 } Z`;     // the foot, back along the boom

/**
 * The JIB: the smaller headsail, in front of the mast.
 *
 * DELIBERATELY not a mirror of the mainsail. It is two thirds the height, its tack
 * is out at the bow rather than at the mast foot, and its curve goes the other way
 * — so the pair reads as two sails on one boat rather than as a symmetrical
 * arrowhead. Mirrored, this is a paper dart.
 */
export const YACHT_JIB =
  // MUCH smaller than the first pass, which set its head at 25 against the
  // mainsail's 17. Eight units apart on a seventy-unit rig is nothing, so the two
  // sails came out the same size either side of the mast and the boat read as a
  // symmetrical ARROWHEAD — exactly the failure this module's own header warns
  // about, written before the drawing that committed it. The head is at 36 now and
  // the jib is a little over half the mainsail's area, which is the proportion a
  // real sloop carries.
  `M${ MAST_X },36`
  + ` C74,48 86,68 89,${ DECK + 1 }`     // the luff, convex, running out to the bow
  // both sails' feet end BELOW the deck line rather than on it. Ended above it —
  // the first pass — there was a wedge of daylight between the jib and the bow,
  // and a gap in a flat glyph is a mistake rather than a style.
  //
  // Its inner edge also runs to MAST_X - 1 rather than MAST_X + 3, so it OVERLAPS
  // the mainsail by a unit instead of starting clear of it. Two units of clearance
  // either side of a 2.6-unit mast left about a unit of background showing between
  // the sails, top to bottom — a hairline of sky down the middle of the boat.
  + ` L${ MAST_X - 1 },${ DECK + 1 } Z`; // the foot, back to the mast

/** The whole boat as ONE path — hull, rig and both sails. This is what the idle
 *  tab and the unentered passcode dot draw. */
export const YACHT = `${ YACHT_HULL } ${ YACHT_MAST } ${ YACHT_MAIN } ${ YACHT_JIB }`;

/**
 * A SIMPLER boat for the wallpaper's smaller grains: hull, mast and mainsail, no
 * jib.
 *
 * Same reason Tallinn's simple flake exists. Below about fifty units the gap
 * between the two sails is under a pixel and they merge into one lumpy triangle —
 * which is worse than one clean sail, because a lump has no name.
 */
export const YACHT_SIMPLE = `${ YACHT_HULL } ${ YACHT_MAST } ${ YACHT_MAIN }`;

/**
 * A RIPPLE — the wallpaper's second element, lying flat between the boats.
 *
 * Kept from the Opera House pass unchanged, and it is the one piece of that design
 * worth keeping: the harbour is the same harbour whatever is floating on it, and a
 * flat lens is the only mark that reads as water without drawing a wave.
 */
export const YACHT_RIPPLE = ( () => {
  const lens = ( cx: number, cy: number, rx: number, ry: number ): string =>
    `M${ f( cx - rx ) },${ f( cy ) }`
    + ` C${ f( cx - rx * 0.5 ) },${ f( cy - ry ) } ${ f( cx + rx * 0.5 ) },${ f( cy - ry ) } ${ f( cx + rx ) },${ f( cy ) }`
    + ` C${ f( cx + rx * 0.5 ) },${ f( cy + ry ) } ${ f( cx - rx * 0.5 ) },${ f( cy + ry ) } ${ f( cx - rx ) },${ f( cy ) } Z`;
  return lens( 0, 0, 15, 2.6 );
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered boats, on the PALE HARBOUR upper zone this skin's passcode
 * screen paints (#EAF6FA). DARK, like every light skin in this family.
 *
 * They go DEEPER, the way water does away from a beach: shallow over sand, then
 * the harbour proper, then the channel, then the deep.
 *
 * IT CLIMBS ALL THE WAY, and that is the fix. The fourth slot used to be the
 * bridge's ochre — the one warm thing in this theme — and the ramp came out 2.26
 * / 3.19 / 5.78 / 3.41 on the zone: it rose for three digits and fell back on the
 * last. A ramp that climbs and then drops does not read as a ramp, it reads as a
 * fault. The gold had a second problem too: a warm mark advances against a cool
 * field, so the FOURTH digit looked like the selected one.
 *
 * Four harbour blues now, 2.26 / 3.19 / 4.49 / 5.78, monotonic and deepest last.
 * The sandstone keeps the places it belongs — the quay, the headland — where
 * there is nothing for it to outrank.
 */
export const YACHT_PC_COLORS = [ '#6FB6D0', '#1E9BC9', '#15819F', '#0B6D95' ];

/**
 * The JIB — the RIGHT-hand sail — per digit, in YELLOW (owner 2026-08-15).
 *
 * The boat is three colours now: a blue mainsail on the left, a yellow jib on the
 * right, and the hull deeper than either. It used to be one blue at three
 * exposures, which is the safe thing to do and also the reason the glyph read as
 * a monochrome mark rather than as a boat with two sails on it.
 *
 * The yellow is not decoration borrowed from nowhere: the owner's sampled skin
 * puts a BRIGHT YELLOW on the outgoing chat bubble, so it is already the second
 * colour of this theme on the phone, and putting it on the sail is what ties the
 * glyph to the skin rather than to the palette.
 *
 * PAIRED BY DEPTH, one for one with the blues: pale blue takes pale yellow, deep
 * blue takes deep amber (1.50 / 1.82 / 2.42 / 3.89 on the white zone against the
 * mainsails' 2.26 / 3.19 / 4.49 / 5.78). Each jib stays UNDER its own mainsail,
 * which is what keeps the mainsail the sail you read first — a yellow that
 * outweighs its blue turns every boat into a jib with a boat attached.
 */
export const YACHT_PC_JIBS = [ '#F0D169', '#E3BC3C', '#C9A227', '#9E7D12' ];

/**
 * ONE tone for every unentered boat — the frame never changes per digit (the rule
 * Aurora's orbs settled).
 *
 * 1.38:1 on the pale harbour. Quiet, because on a LIGHT skin the unentered dots
 * are the ones that shout if you let them.
 */
export const YACHT_PC_OFF = '#BBD4DE';

/**
 * A per-digit lean, alternating side, about the HULL.
 *
 * WIDER than the building this replaced, which was pegged to five degrees because
 * a leaning building is a collapsing one. A yacht under sail HEELS — it leans away
 * from the wind, and a boat drawn dead upright is a boat in a marina.
 */
export const YACHT_PC_TILTS = [ -9, 7, -5, 11 ];
